import React, { useRef, useState, useEffect } from "react";
import {
  MessageSquare,
  User,
  Users,
  Building2,
  Search,
  Pin,
  Settings,
  Reply,
  CheckCheck,
  FileText,
  Download,
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  AtSign,
  Paperclip,
  Smile,
  Send,
  Circle,
  Check,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const InteractiveDemo = () => {
  const [selectedChat, setSelectedChat] = useState("mike");
  const [message, setMessage] = useState("");
  const [selectTab, setSelectTab] = useState("All");
  const [activeView, setActiveView] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [messages, setMessages] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const messagesEndRef = useRef(null);
  const [messageInput, setMessageInput] = useState("");
  const messageIdCounter = useRef(0);

  const sendMessage = () => {
    if (!messageInput.trim()) return;

    const newMessage = {
      id: messageIdCounter.current++,
      sender: "You",
      content: messageInput,
      timestamp: new Date(),
      status: "sending",
      replyTo: replyingTo
        ? { sender: replyingTo.sender, content: replyingTo.content }
        : undefined,
    };

    setMessages([...messages, newMessage]);
    setMessageInput("");
    setReplyingTo(null);

    // Animate status
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "sent" } : msg,
        ),
      );
    }, 300);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "delivered" } : msg,
        ),
      );
    }, 800);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "read" } : msg,
        ),
      );
    }, 1500);
  };
  useEffect(() => {
    if (!selectedConversation) return;

    // Reset messages and counter when conversation changes
    setMessages([]);
    messageIdCounter.current = 0;
    setShowTypingIndicator(false);

    let currentIndex = 0;
    let isCancelled = false;
    const timeouts = [];

    const playNextMessage = () => {
      if (isCancelled || currentIndex >= demoMessages.length) return;

      const demoMsg = demoMessages[currentIndex];

      // Show typing indicator for Sarah
      if (demoMsg.sender !== "You") {
        setShowTypingIndicator(true);
        const timeout1 = setTimeout(() => {
          if (isCancelled) return;
          setShowTypingIndicator(false);
          addMessage(demoMsg);
          currentIndex++;
          if (currentIndex < demoMessages.length) {
            const timeout2 = setTimeout(playNextMessage, 1500);
            timeouts.push(timeout2);
          }
        }, 2000);
        timeouts.push(timeout1);
      } else {
        addMessage(demoMsg);
        currentIndex++;
        if (currentIndex < demoMessages.length) {
          const timeout3 = setTimeout(playNextMessage, 1500);
          timeouts.push(timeout3);
        }
      }
    };

    const initialTimeout = setTimeout(playNextMessage, 1000);
    timeouts.push(initialTimeout);

    // Cleanup function
    return () => {
      isCancelled = true;
      timeouts.forEach((timeout) => clearTimeout(timeout));
      setShowTypingIndicator(false);
    };
  }, [selectedConversation]);
  const addMessage = (demoMsg) => {
    const newMessage = {
      id: messageIdCounter.current++,
      sender: demoMsg.sender,
      content: demoMsg.content,
      timestamp: new Date(),
      status: "sending",
      attachments: demoMsg.attachments,
    };

    setMessages((prev) => [...prev, newMessage]);

    // Animate status changes
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "sent" } : msg,
        ),
      );
    }, 500);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "delivered" } : msg,
        ),
      );
    }, 1000);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === newMessage.id ? { ...msg, status: "read" } : msg,
        ),
      );
    }, 1500);
  };
  const [conversations] = useState([
    {
      id: 1,
      name: "Sarah Williams",
      type: "user",
      avatar: "SW",
      lastMessage: "Thanks for the update on the project!",
      timestamp: "2m ago",
      unread: 2,
      online: true,
      pinned: true,
    },
    {
      id: 2,
      name: "Project Alpha Team",
      type: "user-group",
      avatar: "PA",
      lastMessage: "John: Meeting at 3 PM today",
      timestamp: "15m ago",
      unread: 5,
      tags: ["#project-alpha", "#urgent"],
    },
    {
      id: 3,
      name: "Acme Corp",
      type: "client",
      avatar: "AC",
      lastMessage: "We need the Q4 report by Friday",
      timestamp: "1h ago",
      unread: 0,
      online: false,
    },
    {
      id: 4,
      name: "Mike Johnson",
      type: "user",
      avatar: "MJ",
      lastMessage: "Can you review the document?",
      timestamp: "Yesterday",
      unread: 0,
      online: true,
    },
  ]);
  const filteredConversations = conversations.filter((conv) => {
    if (activeView === "dms") return conv.type === "user";
    if (activeView === "groups")
      return conv.type === "user-group" || conv.type === "client-group";
    if (activeView === "clients") return conv.type === "client";
    if (searchQuery) {
      return (
        conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return true;
  });

  // Chat data
  const chats = {
    all: [
      {
        id: "sarah",
        initials: "SW",
        name: "Sarah Williams",
        lastMessage: "Thanks for the update on the project!",
        time: "2m ago",
        unread: 2,
        online: true,
        pinned: true,
        type: "direct", // Add this field
      },
      {
        id: "alpha",
        initials: "PA",
        name: "Project Alpha Team",
        lastMessage: "John: Meeting at 3 PM today",
        time: "15m ago",
        unread: 5,
        tags: ["#project-alpha", "#urgent"],
        type: "group", // Add this field
      },
      {
        id: "acme",
        initials: "AC",
        name: "Acme Corp",
        lastMessage: "We need the Q4 report by Friday",
        time: "1h ago",
        type: "client", // Add this field
      },
      {
        id: "mike",
        initials: "MJ",
        name: "Mike Johnson",
        lastMessage: "Can you review the document?",
        time: "Yesterday",
        online: true,
        type: "direct", // Add this field
      },
    ],
  };

  // Messages for selected chat
  const demoMessages = [
    {
      id: 1,
      sender: "sarah",
      content: "Hey! How is the project coming along?",
      time: "06:28 PM",
      status: "read",
    },
    {
      id: 2,
      sender: "me",
      content: "Going well! Just finished the design phase.",
      time: "06:28 PM",
      status: "read",
    },
    {
      id: 3,
      sender: "sarah",
      content: "Awesome! Can you share the files?",
      time: "06:28 PM",
      status: "read",
    },
    {
      id: 4,
      sender: "me",
      content: "Sure, here are the design files",
      time: "06:28 PM",
      status: "read",
      attachment: {
        name: "Design_V2.pdf",
        size: "2.4 MB",
      },
    },
  ];
  // const FilterTab = ()=>{
  //   setSelectTab === "All" ? chats : chats.filter((item)=>item.)
  // }

  const GetfilterChats = () => {
    switch (selectTab) {
      case "All":
        return chats.all;
      case "Direct":
        return chats.all.filter((item) => item.type === "direct");
      case "Groups":
        return chats.all.filter((item) => item.type === "group");
      case "Clients":
        return chats.all.filter((item) => item.type === "client");
      default:
        return chats.all;
    }
  };

  const filterChats = GetfilterChats();
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      // Handle send message
      setMessage("");
    }
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case "sending":
        return <Circle className="w-3 h-3 text-gray-400 animate-pulse" />;
      case "sent":
        return <Check className="w-3 h-3 text-gray-400" />;
      case "delivered":
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case "read":
        return <CheckCheck className="w-3 h-3 text-cyan-500" />;
      default:
        return null;
    }
  };
  const addReaction = (messageId, emoji) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || [];
          const existingReaction = reactions.find((r) => r.emoji === emoji);

          if (existingReaction) {
            if (existingReaction.users.includes("You")) {
              return {
                ...msg,
                reactions: reactions
                  .map((r) =>
                    r.emoji === emoji
                      ? {
                          ...r,
                          users: r.users.filter((u) => u !== "You"),
                          count: r.count - 1,
                        }
                      : r,
                  )
                  .filter((r) => r.count > 0),
              };
            } else {
              return {
                ...msg,
                reactions: reactions.map((r) =>
                  r.emoji === emoji
                    ? { ...r, users: [...r.users, "You"], count: r.count + 1 }
                    : r,
                ),
              };
            }
          } else {
            return {
              ...msg,
              reactions: [...reactions, { emoji, users: ["You"], count: 1 }],
            };
          }
        }
        return msg;
      }),
    );
  };
  return (
    <section className="interactive-demo bg-gradient-to-br from-white via-cyan-50 to-blue-50 ">
      {/* Background blobs */}
      <div className="demo-background">
        <div className="blob-light-1"></div>
        <div className="blob-light-2"></div>
      </div>

      <div className="demo-container">
        {/* Header */}
        <div className="Message-demo-header">
          <div className="demo-badge">
            <MessageSquare className="badge-icon" />
            <span className="demo-class-text">Interactive Demo</span>
          </div>
          <h2 className="demo-title">
            Experience Real-Time
            <br />
            <span className="gradient-text-cyan">Messaging</span>
          </h2>
          <p className="demo-subtitle">
            Click on a conversation to see the magic happen. Send messages, add
            reactions, and explore all features.
          </p>
        </div>

        {/* Chat Interface */}
        {/* <div className="chat-interface">
          <div className="chat-sidebar">
            <nav className="sidebar-nav">
              <button
                className={`nav-item ${selectTab === "All" ? "active" : ""}`}
                onClick={() => setSelectTab("All")}
              >
                <MessageSquare className="nav-icon" />
                <span className="nav-item-Text">All </span>
              </button>
              <button
                className={`nav-item ${selectTab === "Direct" ? "active" : ""}`}
                onClick={() => setSelectTab("Direct")}
              >
                <User className="nav-icon" />
                <span>Direct</span>
              </button>
              <button
                className={`nav-item ${selectTab === "Groups" ? "active" : ""}`}
                onClick={() => setSelectTab("Groups")}
              >
                <Users className="nav-icon" />
                <span>Groups</span>
              </button>
              <button
                className={`nav-item ${selectTab === "Clients" ? "active" : ""}`}
                onClick={() => setSelectTab("Clients")}
              >
                <Building2 className="nav-icon" />
                <span>Clients</span>
              </button>
            </nav>
          </div>

          <div className="chat-list">
            <div className="chat-search">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search..."
                className="search-input"
              />
            </div>
            <div className="chat-list-scroll">
              {filterChats.map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-item ${selectedChat === chat.id ? "active" : ""}`}
                  onClick={() => setSelectedChat(chat.id)}
                >
                  <div className="chat-avatar">
                    <div className="avatar-gradient">{chat.initials}</div>
                    {chat.online && <span className="online-indicator"></span>}
                  </div>
                  <div className="chat-info">
                    <div className="chat-header">
                      <div className="chat-name-wrapper">
                        <h4 className="chat-name">{chat.name}</h4>
                        {chat.pinned && <Pin className="pin-icon" />}
                      </div>
                      <span className="chat-time">{chat.time}</span>
                    </div>
                    <p className="chat-last-message">{chat.lastMessage}</p>
                    <div className="chat-footer">
                      {chat.tags && (
                        <div className="chat-tags">
                          {chat.tags.map((tag, index) => (
                            <span key={index} className="tag">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {chat.unread && (
                        <span className="unread-badge">{chat.unread}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="chat-window">
            <div className="window-header">
              <div className="header-left">
                <div className="header-avatar">
                  <div className="avatar-gradient small">MJ</div>
                  <span className="online-indicator small"></span>
                </div>
                <div className="header-info">
                  <h3 className="header-name">Mike Johnson</h3>
                  <p className="header-status">Online</p>
                </div>
              </div>
              <Settings className="header-settings" />
            </div>

            <div className="messages-area">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-wrapper ${msg.sender === "me" ? "own" : "other"}`}
                >
                  {msg.sender !== "me" && (
                    <span className="message-sender">Sarah Williams</span>
                  )}
                  <div className="message-group">
                    <div
                      className={`message-bubble ${msg.sender === "me" ? "own" : "other"}`}
                    >
                      <p className="message-content">{msg.content}</p>
                      {msg.attachment && (
                        <div className="message-attachment">
                          <FileText className="attachment-icon" />
                          <div className="attachment-info">
                            <p className="attachment-name">
                              {msg.attachment.name}
                            </p>
                            <p className="attachment-size">
                              {msg.attachment.size}
                            </p>
                          </div>
                          <Download className="download-icon" />
                        </div>
                      )}
                    </div>

                    <div className="reaction-toolbar">
                      <button className="reaction-btn">👍</button>
                      <button className="reaction-btn">❤️</button>
                      <button className="reaction-btn">😂</button>
                      <div className="toolbar-divider"></div>
                      <button className="reaction-btn">
                        <Reply className="reply-icon" />
                      </button>
                    </div>
                  </div>
                  <div className="message-meta">
                    <span className="message-time">{msg.time}</span>
                    {msg.sender === "me" && (
                      <CheckCheck className="message-status" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="message-input-area">
              <div className="formatting-toolbar">
                <button className="format-btn">
                  <Bold className="format-icon" />
                </button>
                <button className="format-btn">
                  <Italic className="format-icon" />
                </button>
                <button className="format-btn">
                  <Strikethrough className="format-icon" />
                </button>
                <button className="format-btn">
                  <List className="format-icon" />
                </button>
                <button className="format-btn">
                  <ListOrdered className="format-icon" />
                </button>
                <button className="format-btn">
                  <AtSign className="format-icon" />
                </button>
              </div>

              <form onSubmit={handleSendMessage} className="input-form">
                <button type="button" className="input-btn">
                  <Paperclip className="btn-icon" />
                </button>
                <div className="input-wrapper">
                  <textarea
                    rows="1"
                    placeholder="Type your message..."
                    className="message-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <button type="button" className="input-btn">
                  <Smile className="btn-icon" />
                </button>
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!message.trim()}
                >
                  <Send className="send-icon" />
                </button>
              </form>
            </div>
          </div>
        </div> */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl border-2 border-cyan-200 shadow-2xl overflow-hidden"
          style={{ height: "700px" }}
        >
          <div className="grid grid-cols-12 h-full ">
            {/* Left Sidebar */}
            <div className="col-span-2 bg-slate-50/80 backdrop-blur-sm border-r border-gray-200 p-4">
              <div className="space-y-2">
                {[
                  { id: "all", icon: MessageSquare, label: "All Chats" },
                  { id: "dms", icon: User, label: "Direct" },
                  { id: "groups", icon: Users, label: "Groups" },
                  { id: "clients", icon: Building2, label: "Clients" },
                ].map((item) => (
                  <motion.button
                    key={item.id}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveView(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeView === item.id
                        ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-700 border border-cyan-300 shadow-sm"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Middle - Conversations */}
            <div className="col-span-3 bg-white/50 backdrop-blur-sm border-r border-gray-200 flex flex-col">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                  />
                </div>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                <AnimatePresence mode="popLayout">
                  {filteredConversations.map((conv, index) => (
                    <motion.div
                      key={conv.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{
                        backgroundColor: "rgba(6, 182, 212, 0.05)",
                      }}
                      onClick={() => setSelectedConversation(conv)}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-all ${
                        selectedConversation?.id === conv.id
                          ? "bg-cyan-50 border-l-4 border-l-cyan-500"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-medium shadow-lg"
                          >
                            {conv.avatar}
                          </motion.div>
                          {conv.online && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {conv.name}
                              </h4>
                              {conv.pinned && (
                                <Pin className="w-3 h-3 text-cyan-500" />
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {conv.timestamp}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 truncate mb-1">
                            {conv.lastMessage}
                          </p>

                          <div className="flex items-center justify-between">
                            {conv.tags && (
                              <div className="flex gap-1">
                                {conv.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs text-cyan-600"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {conv.unread > 0 && (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="ml-auto px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full"
                              >
                                {conv.unread}
                              </motion.span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Right - Chat Window */}
            <div className="col-span-7 flex flex-col bg-gradient-to-br from-white/50 to-cyan-50/30 backdrop-blur-sm">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-lg">
                            {selectedConversation.avatar}
                          </div>
                          {selectedConversation.online && (
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            {selectedConversation.name}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {selectedConversation.online ? "Online" : "Offline"}
                            {showTypingIndicator &&
                              selectedConversation.online && (
                                <motion.span
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="text-cyan-600"
                                >
                                  {" • typing"}
                                  <motion.span
                                    animate={{ opacity: [0, 1, 0] }}
                                    transition={{
                                      duration: 1.5,
                                      repeat: Infinity,
                                    }}
                                  >
                                    ...
                                  </motion.span>
                                </motion.span>
                              )}
                          </p>
                        </div>
                      </div>

                      <Settings className="w-5 h-5 text-gray-400 cursor-pointer hover:text-cyan-600 transition-colors" />
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <AnimatePresence mode="popLayout">
                      {messages.map((message, index) => (
                        <motion.div
                          key={message.id}
                          initial={{ opacity: 0, y: 20, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.3 }}
                          className={`flex ${message.sender === "You" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-md ${message.sender === "You" ? "items-end" : "items-start"} flex flex-col gap-1`}
                          >
                            {message.sender !== "You" && (
                              <span className="text-xs text-gray-500 px-2">
                                {message.sender}
                              </span>
                            )}

                            {message.replyTo && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="px-4 py-2 bg-gray-100 border-l-2 border-cyan-500 rounded-lg mb-1"
                              >
                                <p className="text-xs text-gray-600">
                                  {message.replyTo.sender}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {message.replyTo.content}
                                </p>
                              </motion.div>
                            )}

                            <div className="relative group">
                              <motion.div
                                whileHover={{ scale: 1.02 }}
                                className={`px-4 py-3 rounded-2xl shadow-sm ${
                                  message.sender === "You"
                                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                                    : "bg-white border border-gray-200 text-gray-900"
                                }`}
                              >
                                <p className="text-sm">{message.content}</p>

                                {message.attachments && (
                                  <div className="mt-2 space-y-2">
                                    {message.attachments.map((file, idx) => (
                                      <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className={`flex items-center gap-2 p-2 rounded-lg ${
                                          message.sender === "You"
                                            ? "bg-black/20"
                                            : "bg-gray-100"
                                        }`}
                                      >
                                        <FileText className="w-4 h-4" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs truncate">
                                            {file.name}
                                          </p>
                                          <p className="text-xs opacity-70">
                                            {file.size}
                                          </p>
                                        </div>
                                        <Download className="w-4 h-4 cursor-pointer hover:scale-110 transition-transform" />
                                      </motion.div>
                                    ))}
                                  </div>
                                )}
                              </motion.div>

                              {/* Quick Actions */}
                              <div className="absolute top-0 right-0 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200 shadow-lg">
                                {["👍", "❤️", "😂"].map((emoji) => (
                                  <motion.button
                                    key={emoji}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      addReaction(message.id, emoji)
                                    }
                                    className="p-1 hover:bg-gray-100 rounded"
                                  >
                                    <span className="text-sm">{emoji}</span>
                                  </motion.button>
                                ))}
                                <div className="w-px h-4 bg-gray-200" />
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => setReplyingTo(message)}
                                  className="p-1 hover:bg-gray-100 rounded"
                                >
                                  <Reply className="w-3 h-3 text-gray-600" />
                                </motion.button>
                              </div>

                              {/* Reactions */}
                              {message.reactions &&
                                message.reactions.length > 0 && (
                                  <div className="flex gap-1 mt-1">
                                    <AnimatePresence>
                                      {message.reactions.map((reaction) => (
                                        <motion.div
                                          key={`${message.id}-${reaction.emoji}`}
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          exit={{ scale: 0 }}
                                          whileHover={{ scale: 1.15 }}
                                          className="px-2 py-0.5 bg-white border border-gray-200 rounded-full text-xs flex items-center gap-1 cursor-pointer shadow-sm"
                                        >
                                          <span>{reaction.emoji}</span>
                                          <motion.span
                                            key={reaction.count}
                                            initial={{ scale: 1.5 }}
                                            animate={{ scale: 1 }}
                                            className="text-gray-600 font-medium"
                                          >
                                            {reaction.count}
                                          </motion.span>
                                        </motion.div>
                                      ))}
                                    </AnimatePresence>
                                  </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2 px-2">
                              <span className="text-xs text-gray-500">
                                {message.timestamp.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              {message.sender === "You" && (
                                <motion.div
                                  key={message.status}
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                >
                                  {getStatusIcon(message.status)}
                                </motion.div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {/* Typing Indicator */}
                    <AnimatePresence>
                      {showTypingIndicator && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="flex justify-start"
                        >
                          <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                            <div className="flex gap-1">
                              {[0, 1, 2].map((i) => (
                                <motion.div
                                  key={i}
                                  animate={{ y: [0, -5, 0] }}
                                  transition={{
                                    duration: 0.6,
                                    repeat: Infinity,
                                    delay: i * 0.2,
                                  }}
                                  className="w-2 h-2 bg-gray-400 rounded-full"
                                />
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Banner */}
                  <AnimatePresence>
                    {replyingTo && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="px-6 py-2 bg-cyan-50 border-t border-cyan-200 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Reply className="w-4 h-4 text-cyan-600" />
                          <div>
                            <p className="text-xs text-cyan-700 font-medium">
                              Replying to {replyingTo.sender}
                            </p>
                            <p className="text-xs text-gray-600 truncate max-w-md">
                              {replyingTo.content}
                            </p>
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setReplyingTo(null)}
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Input */}
                  <div className="p-4 border-t border-gray-200 bg-white/80 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                      {[
                        Bold,
                        Italic,
                        Strikethrough,
                        List,
                        ListOrdered,
                        AtSign,
                      ].map((Icon, idx) => (
                        <motion.button
                          key={idx}
                          whileHover={{
                            scale: 1.1,
                            backgroundColor: "rgba(6, 182, 212, 0.1)",
                          }}
                          whileTap={{ scale: 0.9 }}
                          className="p-1.5 rounded-lg transition-colors"
                        >
                          <Icon className="w-4 h-4 text-gray-500" />
                        </motion.button>
                      ))}
                    </div>

                    <div className="flex items-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <Paperclip className="w-5 h-5 text-gray-500" />
                      </motion.button>

                      <div className="flex-1">
                        <textarea
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          placeholder="Type your message..."
                          rows={1}
                          className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-200"
                        />
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                      >
                        <Smile className="w-5 h-5 text-gray-500" />
                      </motion.button>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={sendMessage}
                        disabled={!messageInput.trim()}
                        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <Send className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Select a conversation to start messaging
                    </p>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractiveDemo;
